import mapboxgl from '../mapboxClient';

export const SITE_MARKER_LAYER = 'aps-site-markers';
const SOURCE = 'aps-site-marker-points';
const ICON_SIZE = 64;
export type SiteMarkerPoint = {
  id: string; name: string; longitude: number; latitude: number;
  iconUrl: string; size: number; priority: number;
};

export function isSiteMarkerHit(map: mapboxgl.Map, point: mapboxgl.PointLike) {
  return !!map.getLayer(SITE_MARKER_LAYER) &&
    map.queryRenderedFeatures(point, { layers: [SITE_MARKER_LAYER] }).length > 0;
}

/** One GPU layer for static sites; DOM is only used for the hovered popup. */
export function attachSiteMarkerLayer(map: mapboxgl.Map, onSelect: (id: string) => void) {
  let points: SiteMarkerPoint[] = [];
  let visible = true;
  let disposed = false;
  let signature = '';
  let popup: mapboxgl.Popup | null = null;
  let hovered: string | null = null;
  const icons = new globalThis.Map<string, { id: string; data?: ImageData; pending: boolean }>();
  let iconSequence = 0;

  function hidePopup() {
    popup?.remove(); popup = null; hovered = null;
    map.getCanvas().style.cursor = '';
  }

  function iconFor(url: string) {
    const existing = icons.get(url);
    if (existing) return existing;
    const entry = { id: `aps-site-icon-${iconSequence++}`, pending: true, data: undefined as ImageData | undefined };
    icons.set(url, entry);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const finish = (loaded: boolean) => {
      if (disposed) return;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = ICON_SIZE;
      const ctx = canvas.getContext('2d')!;
      try {
        if (!loaded || !img.naturalWidth) throw new Error('Image unavailable');
        const scale = ICON_SIZE / Math.max(img.naturalWidth, img.naturalHeight);
        const width = img.naturalWidth * scale, height = img.naturalHeight * scale;
        ctx.drawImage(img, (ICON_SIZE - width) / 2, (ICON_SIZE - height) / 2, width, height);
        entry.data = ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
      } catch {
        // A missing or non-CORS image must not make a site unselectable.
        canvas.width = ICON_SIZE;
        ctx.fillStyle = '#8b0000'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.arc(32, 32, 23, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        entry.data = ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
      }
      entry.pending = false;
      restore();
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
    return entry;
  }

  function restore(force = false) {
    if (disposed || (!force && !map.isStyleLoaded() && !map.getLayer(SITE_MARKER_LAYER))) return;
    for (const point of points) iconFor(point.iconUrl);
    for (const entry of icons.values()) {
      if (entry.data && !map.hasImage(entry.id)) map.addImage(entry.id, entry.data, { pixelRatio: 2 });
    }
    const features: GeoJSON.Feature<GeoJSON.Point>[] = points.flatMap(point => {
      const icon = icons.get(point.iconUrl);
      return icon?.data ? [{ type: 'Feature', id: point.id, properties: {
        siteId: point.id, name: point.name, icon: icon.id, size: point.size / 32, priority: point.priority,
      }, geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] } }] : [];
    });
    const data: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features };
    const nextSignature = JSON.stringify(data);
    const source = map.getSource(SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!source) map.addSource(SOURCE, { type: 'geojson', data });
    else if (force || signature !== nextSignature) source.setData(data);
    signature = nextSignature;
    if (!map.getLayer(SITE_MARKER_LAYER)) map.addLayer({
      id: SITE_MARKER_LAYER, source: SOURCE, type: 'symbol', slot: 'top',
      layout: {
        'icon-image': ['get', 'icon'], 'icon-size': ['get', 'size'],
        'icon-allow-overlap': true, 'icon-ignore-placement': true,
        'icon-pitch-alignment': 'viewport', 'icon-rotation-alignment': 'viewport',
        'symbol-sort-key': ['get', 'priority'], 'symbol-z-order': 'source',
      },
      paint: { 'icon-opacity': 1, 'icon-emissive-strength': 1, 'icon-occlusion-opacity': 1 },
    });
    const visibility = visible ? 'visible' : 'none';
    if (map.getLayoutProperty(SITE_MARKER_LAYER, 'visibility') !== visibility)
      map.setLayoutProperty(SITE_MARKER_LAYER, 'visibility', visibility);
  }

  function click(event: mapboxgl.MapLayerMouseEvent) {
    if (event.defaultPrevented) return;
    const id = event.features?.[0]?.properties?.siteId;
    if (typeof id === 'string') { event.preventDefault(); hidePopup(); onSelect(id); }
  }
  function hover(event: mapboxgl.MapLayerMouseEvent) {
    const feature = event.features?.[0];
    if (!feature || feature.geometry.type !== 'Point') return;
    const id = String(feature.properties?.siteId);
    if (hovered === id) return;
    hidePopup(); hovered = id; map.getCanvas().style.cursor = 'pointer';
    const content = document.createElement('div');
    content.className = 'site-popup'; content.textContent = String(feature.properties?.name || '');
    popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 18, anchor: 'bottom' })
      .setLngLat(feature.geometry.coordinates as [number, number]).setDOMContent(content).addTo(map);
  }
  const styleLoaded = () => restore(true);
  const idle = () => restore();
  map.on('style.load', styleLoaded);
  // Image loads may finish while a style is still loading its sources.
  map.on('idle', idle);
  map.on('click', SITE_MARKER_LAYER, click);
  map.on('mousemove', SITE_MARKER_LAYER, hover);
  map.on('mouseleave', SITE_MARKER_LAYER, hidePopup);
  map.on('movestart', hidePopup);
  return {
    update(next: SiteMarkerPoint[], show: boolean) {
      points = next.filter(p => Number.isFinite(p.longitude) && Number.isFinite(p.latitude));
      visible = show; hidePopup(); restore();
    },
    dispose(removeResources = true) {
      disposed = true; hidePopup();
      map.off('style.load', styleLoaded); map.off('idle', idle);
      map.off('click', SITE_MARKER_LAYER, click); map.off('mousemove', SITE_MARKER_LAYER, hover);
      map.off('mouseleave', SITE_MARKER_LAYER, hidePopup); map.off('movestart', hidePopup);
      // When the whole map is being destroyed, let Mapbox tear down terrain
      // and sources together rather than removing a source mid-import.
      if (removeResources) {
        if (map.getLayer(SITE_MARKER_LAYER)) map.removeLayer(SITE_MARKER_LAYER);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
        for (const icon of icons.values()) if (map.hasImage(icon.id)) map.removeImage(icon.id);
      }
      icons.clear();
    },
  };
}
